import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { useBandContext, usePermissionsContext } from "Bands/contexts";
import { useClearActionMetaOnUnMount, usePagination } from "Bands/utils";
import { Card, Loader, Pager } from "shared/components";
import { Content } from "shared/styles";
import { Lock } from "shared/assets/icons";
import {
  dataMetaSelector,
  dispatchGetBandUsersPermissions,
  groupPermissionsSelectorCreator,
} from "Bands/state";
import { BAND_ACTIONS } from "Bands/types";
import { PermissionsTable } from "./containers";
import { IAPIState } from "shared/types";
import { FourOhFour } from "shared/pages";
import { API_ENDPOINTS } from "shared/constants";
import { Spacer } from "shared/layout";

export const PermissionGroupPage: FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const { rootGroups, rootGroupPermissions } = usePermissionsContext();
  const { bandId } = useBandContext();
  const dispatch = useDispatch();

  const rootGroup = useMemo(
    () => rootGroups?.find(({ id }) => id === groupId),
    [groupId, rootGroups]
  );

  const groupIndex = useMemo(
    () => rootGroups?.findIndex(({ id }) => id === groupId),
    [groupId, rootGroups]
  );

  const permissions = useMemo(
    () => rootGroupPermissions?.[groupId],
    [groupId, rootGroupPermissions]
  );

  const groupPermissionsSelector = useMemo(
    () => groupPermissionsSelectorCreator(groupId),
    [groupId]
  );
  const groupPermissions = useSelector(groupPermissionsSelector);

  const pagination = usePagination(API_ENDPOINTS.PERMISSION_GROUP(groupId));

  // TODO: why permissionTypes changes after fetch?
  const fetchedOnce = useRef<{ [groupId: string]: boolean }>({});
  const { api: apiMeta } = useSelector(dataMetaSelector(BAND_ACTIONS.getBandUsersPermissions));
  useClearActionMetaOnUnMount(BAND_ACTIONS.getBandUsersPermissions);

  const fetchPermissions = useCallback(
    (page?: number) => {
      if (!bandId || !permissions?.length) return;

      dispatchGetBandUsersPermissions({
        dispatch,
        page,
        bandId,
        permissionTypes: permissions.map(({ id }) => id),
        rootGroupId: groupId,
      });
    },
    [bandId, dispatch, groupId, permissions]
  );

  useEffect(() => {
    if (!bandId || !permissions?.length || fetchedOnce.current?.[groupId]) return;

    fetchedOnce.current = {
      ...fetchedOnce.current,
      [groupId]: true,
    };

    fetchPermissions();
  }, [bandId, permissions, groupId, fetchPermissions]);

  const renderContent = () => {
    if (apiMeta?.status === IAPIState.pending && !groupPermissions) {
      return <Loader />;
    }

    if (groupPermissions) {
      return <PermissionsTable users={groupPermissions} />;
    }

    return null;
  };

  if (groupIndex === undefined && apiMeta?.status === IAPIState.succeeded) return <FourOhFour />;

  return (
    <Content>
      <Card title={rootGroup?.label} icon={Lock}>
        <Spacer size="s">
          {renderContent()}
          <Pager {...pagination} onPageChange={fetchPermissions} />
        </Spacer>
      </Card>
    </Content>
  );
};
